import { sanitizeNodeError, type NodeErrorTranslate } from '../../../../utils/sanitizeNodeError'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'

/**
 * Which input produced the banner message. Exposed for NAD logging so a trace
 * shows *why* a given message won, not just what was rendered.
 */
export type VideoBannerSource = 'block-reason' | 'node-error' | 'none'

export type VideoBanner = {
  /** Text to render, or `undefined` when the banner should not render at all. */
  message: string | undefined
  /** `title` attribute containing the bounded, display-safe provider detail. */
  tooltip: string | undefined
  source: VideoBannerSource
  /** True when an actionable blocker displaced a stale error (the reported bug). */
  supersededStaleError: boolean
  /** The stale message that was displaced, for logging. */
  supersededErrorText: string | undefined
}

/**
 * Resolve what the Video Generator node's error banner should say.
 *
 * Post 1586850233072274 ("Dead-End 'Generation Timed Out' Error Loops"): the
 * banner used to render only the persisted `error` from the last run. Because
 * `sanitizeNodeError` collapses every timeout into the generic
 * "Generation timed out. Please try again.", a user whose node was blocked by a
 * *client-side* validation (e.g. overlapping video segments) saw a message
 * telling them to retry — while the generate button was disabled and the real,
 * fixable reason was hidden in that button's hover tooltip.
 *
 * The rule: a CURRENT blocker outranks a STALE error. The blocker is actionable
 * and is what is stopping generation right now; `error` describes a run that has
 * already finished.
 *
 * Display-safe provider detail is never discarded — when a block reason takes
 * the message, the sanitized detail is appended to the tooltip. This preserves
 * a provider's specific reason while keeping the sanitizer as the sole UI
 * boundary for integrity errors and structured noise.
 */
export function resolveVideoBanner(args: {
  /** Current, actionable reason generation is blocked (already localized). */
  blockReason: string | undefined
  status: NodeStatus | undefined
  /** Persisted node error from the previous run. */
  error: string | undefined | null
  /** `useTranslation('canvas').t` — localizes the sanitizer's own banner copy. */
  t: NodeErrorTranslate
}): VideoBanner {
  const { blockReason, status, error, t } = args
  const tooltipError = error ? sanitizeNodeError(error, t, true) : undefined

  // A stale error only counts while the node is actually in the error state —
  // otherwise a cleared blocker could resurrect an old message.
  const staleError = status === 'error' ? tooltipError : undefined

  const message = blockReason ?? staleError
  const source: VideoBannerSource = blockReason
    ? 'block-reason'
    : staleError
      ? 'node-error'
      : 'none'

  const tooltip = blockReason && tooltipError
    ? `${blockReason}\n\n${tooltipError}`
    : blockReason ?? tooltipError

  return {
    message,
    tooltip,
    source,
    supersededStaleError: Boolean(blockReason && staleError),
    supersededErrorText: blockReason ? staleError : undefined,
  }
}

/** What occupies the strip over the bottom of the Video Generator preview. */
export type VideoCardOverlay = 'download-preparing' | 'status' | 'none'

/**
 * RFC-1612: the download-preparing banner and the status message share one
 * slot. While the node's video is being signed for download, the download
 * banner wins over any status message (including the "model isn't available"
 * notice); the status message returns when signing ends unless the user
 * dismissed it. Nothing overlays the render spinner.
 */
export function resolveVideoCardOverlay(args: {
  showSpinner: boolean
  hasVideo: boolean
  downloadPreparing: boolean
  bannerMessage: string | undefined
  bannerDismissed: boolean
}): VideoCardOverlay {
  if (args.showSpinner) return 'none'
  if (args.downloadPreparing && args.hasVideo) return 'download-preparing'
  if (args.bannerMessage && !args.bannerDismissed) return 'status'
  return 'none'
}
